import { useCallback, useEffect, useState } from 'react';

import { fetchOrbit } from '../api.js';
import type { OrbitItem } from '../api.js';

/**
 * `ready` with an empty array IS the "nothing to show yet" state, exactly as in
 * useNodes — there is nothing more to say about it than the view itself says.
 */
export type OrbitState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; items: OrbitItem[]; readOnly: boolean };

export interface UseOrbit {
  state: OrbitState;
  /**
   * Re-reads the orbit. Needed because adding, editing or deleting a goal
   * changes where something sits — a goal pulls its node off the watched
   * ring and inward — and the view cannot work that out for itself: the ring a
   * goal belongs on depends on its deadline relative to every other goal.
   */
  reload: () => Promise<void>;
}

/**
 * `spaceId` omitted reads the caller's own orbit. Passed, it reads someone
 * else's — only ever a space the caller has been granted a share on, 404
 * otherwise (surfaced here as the ordinary error state). Changing `spaceId`
 * re-reads: switching who is being viewed must never keep showing the
 * previous person's items while the new ones are still in flight.
 */
export function useOrbit(spaceId?: string): UseOrbit {
  const [state, setState] = useState<OrbitState>({ kind: 'loading' });

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      const { items, readOnly } = await fetchOrbit(spaceId);
      setState({ kind: 'ready', items, readOnly });
    } catch (err) {
      setState({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Could not load your orbit.',
      });
    }
  }, [spaceId]);

  useEffect(() => {
    void load();
  }, [load]);

  return { state, reload: load };
}
